import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, horizontalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LayoutList, Table2, LayoutGrid, Calendar, GanttChart, Grid3x3, CalendarDays,
  Plus, X, Star, Eye, EyeOff, Pencil, Trash2, Lock, Sliders, Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import { useViewFieldConfig } from '../hooks/useViewFieldConfig';

export const VIEW_ICONS = {
  agenda: CalendarDays,
  list: LayoutList,
  table: Table2,
  board: LayoutGrid,
  calendar: Calendar,
  timeline: GanttChart,
  gallery: Grid3x3,
};

// Agenda is home-only (tied to working-day/working-hours settings), so it's
// not offered as a view type elsewhere. Home passes a custom availableTypes.
export const DEFAULT_VIEW_TYPES = ['list', 'table', 'board', 'calendar', 'timeline', 'gallery'];

export function viewLabel(t, type) {
  const key = type === 'board' ? 'tasks.views.kanban' : `tasks.views.${type}`;
  return t(key);
}

// Tabs created without a name are stored with an English name: the type ("Table",
// "Board 2"; project boards start as "Kanban"). Until renamed, show it in the app's language.
const SEEDED_NAMES = { board: ['Board', 'Kanban'] };
export function tabName(t, view) {
  if (!view?.type || !VIEW_ICONS[view.type]) return view?.name;
  const seeded = SEEDED_NAMES[view.type] || [view.type[0].toUpperCase() + view.type.slice(1)];
  const m = /^(.*?)( \d+)?$/.exec(view.name || '');
  return m && seeded.includes(m[1]) ? `${viewLabel(t, view.type)}${m[2] || ''}` : view.name;
}

function SortableTab({ view, name, isActive, locked, onActivate, onMenu, onClose, label, canClose }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: view.id, disabled: locked || view.pinned });

  const { t } = useTranslation();
  const Icon = VIEW_ICONS[view.type];
  const upgradeTitle = t('ui_views.tabs.upgrade_required', { name: label });

  // Long-press → open context menu on touch devices (no native right-click on mobile).
  const longPressRef = React.useRef({ timer: null, fired: false, x: 0, y: 0 });
  const startLongPress = (e) => {
    if (locked) return;
    const t = e.touches?.[0];
    if (!t) return;
    longPressRef.current.fired = false;
    longPressRef.current.x = t.clientX;
    longPressRef.current.y = t.clientY;
    longPressRef.current.timer = setTimeout(() => {
      longPressRef.current.fired = true;
      onMenu({ clientX: longPressRef.current.x, clientY: longPressRef.current.y, preventDefault: () => {}, stopPropagation: () => {} });
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressRef.current.timer) {
      clearTimeout(longPressRef.current.timer);
      longPressRef.current.timer = null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none',
      }}
      {...attributes}
      {...listeners}
      className="group/tab"
    >
      <button
        type="button"
        onClick={(e) => {
          if (isDragging || longPressRef.current.fired) return;
          e.stopPropagation();
          onActivate();
        }}
        onContextMenu={(e) => { e.preventDefault(); onMenu(e); }}
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchMove={cancelLongPress}
        onTouchCancel={cancelLongPress}
        className={cn(
          'flex items-center gap-1.5 px-3 h-7 rounded-full text-xs font-medium transition-colors',
          locked
            ? 'text-muted-foreground/40 cursor-not-allowed'
            : isActive
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent/60',
        )}
        title={locked ? upgradeTitle : name}
      >
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="max-w-[140px] truncate">{name}</span>
        {view.isDefault && !locked && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
        {locked && <Lock className="w-3 h-3 text-amber-500" />}
        {canClose && !locked && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="ml-0.5 hidden md:inline-flex p-0.5 rounded hover:bg-accent opacity-0 group-hover/tab:opacity-100 transition-opacity"
          >
            <X className="w-2.5 h-2.5" />
          </span>
        )}
      </button>
    </div>
  );
}

// Anchored dropdown rendered into document.body, so it isn't clipped by any
// ancestor with overflow:auto/hidden. The tab bar itself sets overflow-x-auto
// to let tabs scroll horizontally on mobile, which per CSS spec also forces
// overflow-y to clip; an in-place `position: absolute` menu inside would be
// invisible. Mirrors the close-on-scroll/resize behaviour of ContextMenu so
// the menu doesn't visually drift away from its trigger.
function PortalMenu({ anchorRef, open, onClose, align = 'left', className, children }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!open) { setRect(null); return; }
    const el = anchorRef.current;
    if (!el) return;
    setRect(el.getBoundingClientRect());
    const close = () => onClose();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !rect) return null;
  // Right-align positions the menu's right edge under the anchor's right edge
  // (used by the Display picker that sits at the far right of the bar).
  const style = align === 'right'
    ? { right: Math.max(8, window.innerWidth - rect.right), top: rect.bottom + 4 }
    : { left: Math.max(8, rect.left), top: rect.bottom + 4 };

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className={cn(
          'fixed z-50 bg-card border border-border rounded-lg shadow-xl py-1',
          className,
        )}
        style={style}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

function ContextMenu({ x, y, items, onClose }) {
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [onClose]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        className="fixed z-50 w-52 bg-card border border-border rounded-lg shadow-xl py-1"
        style={{ left: x, top: y }}
      >
        {items.map((it, i) => it === 'divider' ? (
          <div key={`d-${i}`} className="my-1 border-t border-border" />
        ) : (
          <button
            key={it.key}
            onClick={() => { it.onClick(); onClose(); }}
            disabled={it.disabled}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors',
              it.disabled
                ? 'text-muted-foreground/40 cursor-not-allowed'
                : it.danger
                  ? 'text-red-500 hover:bg-red-500/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
            )}
          >
            {it.icon && <it.icon className="w-3.5 h-3.5" />}
            <span>{it.label}</span>
          </button>
        ))}
      </div>
    </>,
    document.body,
  );
}

const ViewTabsBar = ({
  tabs,
  activeView,
  hiddenCatalog = [],
  isLocked = () => false,
  actions,
  loading = false,
  availableViewTypes = DEFAULT_VIEW_TYPES,
  customFieldDefs = [],
  className,
  // Database views are shared and can't be hidden per person; people who only
  // read can switch views but not change them.
  canHide = true,
  canEdit = true,
}) => {
  const { t } = useTranslation();
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, viewId }
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const addBtnRef = useRef(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = tabs.findIndex(v => v.id === active.id);
    const to = tabs.findIndex(v => v.id === over.id);
    if (from < 0 || to < 0) return;
    const ordered = arrayMove(tabs.map(v => v.id), from, to);
    actions.reorder(ordered);
  };

  const openCtx = (e, viewId) => {
    setCtxMenu({ x: e.clientX, y: e.clientY, viewId });
  };

  const activeTab = tabs.find(t2 => t2.id === ctxMenu?.viewId);

  const menuItems = activeTab ? [
    {
      key: 'default',
      icon: Star,
      label: activeTab.isDefault
        ? t('view_tabs.default_set', 'Default view')
        : t('view_tabs.set_default', 'Set as default'),
      disabled: activeTab.isDefault || activeTab.locked,
      onClick: () => actions.setDefault(activeTab.id),
    },
    {
      key: 'rename',
      icon: Pencil,
      label: t('view_tabs.rename', 'Rename'),
      onClick: () => { setRenamingId(activeTab.id); setRenameValue(tabName(t, activeTab)); },
    },
    ...(canHide ? [{
      key: 'hide',
      icon: EyeOff,
      label: t('view_tabs.hide', 'Hide from my tabs'),
      disabled: tabs.length <= 1 || activeTab.pinned,
      onClick: () => actions.hide(activeTab.id),
    }] : []),
    ...(actions.duplicate ? [{
      key: 'duplicate',
      icon: Plus,
      label: t('view_tabs.duplicate', 'Duplicate view'),
      onClick: () => actions.duplicate(activeTab.id),
    }] : []),
    'divider',
    {
      key: 'delete',
      icon: Trash2,
      label: t('view_tabs.delete', 'Delete view'),
      disabled: tabs.length <= 1 || activeTab.pinned,
      danger: true,
      onClick: () => actions.removeView(activeTab.id),
    },
  ] : [];

  const tabIds = tabs.map(v => v.id);

  if (loading) {
    // Skeleton preserves the tab bar's layout height so there's no jump
    // when real tabs arrive. Three ghost chips is a neutral guess.
    return (
      <div className={cn('flex items-center gap-1 pb-1.5 border-b border-border', className)}>
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="flex items-center gap-1.5 px-3 h-7"
          >
            <div className="w-3.5 h-3.5 rounded-sm bg-muted-foreground/15 animate-pulse" />
            <div className="hidden sm:block w-14 h-3 rounded bg-muted-foreground/15 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-1 pb-1.5 border-b border-border overflow-x-auto scrollbar-none', className)}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={tabIds} strategy={horizontalListSortingStrategy}>
          {tabs.map((view) => renamingId === view.id ? (
            <InlineRename
              key={view.id}
              value={renameValue}
              onChange={setRenameValue}
              onCommit={() => {
                const v = renameValue.trim();
                if (v && v !== tabName(t, view)) actions.renameView(view.id, v);
                setRenamingId(null);
              }}
              onCancel={() => setRenamingId(null)}
            />
          ) : (
            <SortableTab
              key={view.id}
              view={view}
              name={tabName(t, view)}
              isActive={activeView?.id === view.id}
              locked={view.locked}
              label={viewLabel(t, view.type)}
              canClose={canHide && tabs.length > 1 && !view.pinned}
              onActivate={() => actions.setActive(view.id)}
              onMenu={(e) => { if (canEdit) openCtx(e, view.id); }}
              onClose={() => actions.hide(view.id)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Hidden tabs: show small "Unhide" group */}
      {hiddenCatalog.length > 0 && (
        <div className="relative ml-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openCtx(e, '__hidden__'); }}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-muted-foreground/50 hover:text-muted-foreground"
            title={t('view_tabs.hidden_count', { count: hiddenCatalog.length, defaultValue: '{{count}} hidden' })}
          >
            <Eye className="w-3.5 h-3.5" />
            <span>{hiddenCatalog.length}</span>
          </button>
        </div>
      )}

      {/* Add view */}
      <div className={canEdit ? '' : 'hidden'}>
        <button
          ref={addBtnRef}
          type="button"
          // One kind of view only (a database's grids): "+" adds it, no menu with one choice.
          onClick={() => (availableViewTypes.length === 1 ? actions.addView(availableViewTypes[0]) : setShowAddMenu(v => !v))}
          className="flex items-center gap-1 px-2 py-1.5 text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          title={t('view_switcher.add_view')}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <PortalMenu
          anchorRef={addBtnRef}
          open={showAddMenu}
          onClose={() => setShowAddMenu(false)}
          align="left"
          className="w-40"
        >
          {availableViewTypes.map(type => {
            const Icon = VIEW_ICONS[type];
            const locked = isLocked(type);
            return (
              <button
                key={type}
                onClick={() => { actions.addView(type); setShowAddMenu(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent text-left"
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{viewLabel(t, type)}</span>
                {locked && <Lock className="w-3 h-3 ml-auto text-amber-500" />}
              </button>
            );
          })}
        </PortalMenu>
      </div>

      {/* Display fields picker - pushed to the right, only when a non-table,
          non-agenda view is active. Table has a column picker elsewhere. */}
      {activeView && activeView.type !== 'table' && activeView.type !== 'agenda' && (
        <div className="ml-auto">
          <ViewFieldsMenu
            activeView={activeView}
            actions={actions}
            customFieldDefs={customFieldDefs}
          />
        </div>
      )}

      {ctxMenu && ctxMenu.viewId !== '__hidden__' && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={menuItems}
          onClose={() => setCtxMenu(null)}
        />
      )}
      {ctxMenu && ctxMenu.viewId === '__hidden__' && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={hiddenCatalog.map(v => ({
            key: v.id,
            icon: Eye,
            label: t('view_tabs.unhide', { name: tabName(t, v), defaultValue: 'Show {{name}}' }),
            onClick: () => actions.unhide(v.id),
          }))}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
};

function ViewFieldsMenu({ activeView, actions, customFieldDefs }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const { allFields, toggleField } = useViewFieldConfig(activeView, actions, customFieldDefs);

  return (
    <div className="ml-1">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-2 py-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
        title={t('view_fields.display', 'Display fields')}
      >
        <Sliders className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{t('view_fields.display', 'Display')}</span>
      </button>
      <PortalMenu
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        align="right"
        className="w-56 max-h-80 overflow-y-auto"
      >
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground/70">
          {t('view_fields.section_builtin', 'Fields')}
        </div>
        {allFields.filter(f => !f.isCustom).map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => toggleField(f.key)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent text-left"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              {f.visible && <Check className="w-3 h-3 text-amber-500" />}
            </span>
            <span className="flex-1 truncate">{t(f.i18n)}</span>
          </button>
        ))}
        {customFieldDefs.length > 0 && (
          <>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground/70 mt-1 border-t border-border/60 pt-2">
              {t('view_fields.section_custom', 'Custom fields')}
            </div>
            {allFields.filter(f => f.isCustom).map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => toggleField(f.key)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent text-left"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  {f.visible && <Check className="w-3 h-3 text-amber-500" />}
                </span>
                <span className="flex-1 truncate">{f.label}</span>
              </button>
            ))}
          </>
        )}
      </PortalMenu>
    </div>
  );
}

export function InlineRename({ value, onChange, onCommit, onCancel }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit();
        if (e.key === 'Escape') onCancel();
      }}
      className="px-3 h-7 rounded-full text-xs font-medium bg-transparent border border-amber-500 outline-none text-foreground w-32"
    />
  );
}

export default ViewTabsBar;
